import React, { useMemo } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Scatter,
} from "recharts";
import styles from "./efficientFrontierCompareChart.module.scss";
import colorsStore from "shared/store/colorsStore";
import { useTranslation } from "react-i18next";
import { formatItalianNumber } from "utils/formatUtils";
import { useComparisonLabels } from "../../../compare/ComparisonLabelsContext";

type DataPoint = { risk: number; return: number };

type Props = {
  data: DataPoint[];
  selectedPlan1: DataPoint;
  selectedPlan2: DataPoint;
  portfolio1: DataPoint;
  portfolio2: DataPoint;
};

const EfficientFrontierCompareChart: React.FC<Props> = ({
  data,
  selectedPlan1,
  selectedPlan2,
  portfolio1,
  portfolio2,
}) => {
  const { t } = useTranslation();
  const labels = useComparisonLabels();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const sortedData = [...data].sort((a, b) => a.risk - b.risk);

  const chartConfig = useMemo(() => {
    const allPoints = [
      ...data,
      selectedPlan1,
      selectedPlan2,
      portfolio1,
      portfolio2,
    ];

    const maxRisk = Math.ceil(Math.max(...allPoints.map((p) => p.risk)));
    const riskRange = maxRisk;
    const riskStep = Math.ceil(riskRange / 6);
    const riskTicks = Array.from(
      { length: Math.floor(riskRange / riskStep) + 1 },
      (_, i) => i * riskStep,
    );

    const maxReturn = Math.ceil(Math.max(...allPoints.map((p) => p.return)));
    const returnRange = maxReturn;
    const returnStep = Math.ceil(returnRange / 6);
    const returnTicks = Array.from(
      { length: Math.floor(returnRange / returnStep) + 1 },
      (_, i) => i * returnStep,
    );

    return {
      riskDomain: [0, maxRisk],
      returnDomain: [0, maxReturn],
      riskTicks,
      returnTicks,
    };
  }, [data, selectedPlan1, selectedPlan2, portfolio1, portfolio2]);

  const axisStyle = {
    fontSize: "12px",
    fontFamily: "inherit",
    fontWeight: 400,
    fill: colorsConfig.FinecoBlack,
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    fill: colorsConfig.FinecoBlack,
  };

  const CustomLegend = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "20px",
          fontSize: "12px",
          fontFamily: "inherit",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", marginRight: "20px" }}
        >
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: colorsConfig.portfolio1,
              marginRight: "8px",
            }}
          />
          <span>{labels.portfolio1}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: colorsConfig.portfolio2,
              marginRight: "8px",
            }}
          />
          <span>{labels.portfolio2}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <ResponsiveContainer width="100%" height={350} debounce={500}>
        <ComposedChart margin={{ top: 20, right: 20, bottom: 30, left: 15 }}>
          {/* <Legend
          verticalAlign="top"
          height={36}
          wrapperStyle={{
            marginTop: "-10px",
            fontSize: "12px",
            fontFamily: "inherit",
          }}
          iconSize={10}
          iconType="circle"
          formatter={(value) => (
            <span style={{ marginRight: "20px" }}>{value}</span>
          )}
        /> */}
          <CartesianGrid stroke={colorsConfig.FinecoGrayLight1} />
          <XAxis
            type="number"
            dataKey="risk"
            name="VAR ATTESO"
            domain={chartConfig.riskDomain}
            ticks={chartConfig.riskTicks}
            tickFormatter={(value) => `${value}%`}
            label={{
              value: t("portfolio.efficientFrontierBottomLabel"),
              position: "insideBottom",
              dy: 25,
              ...labelStyle,
            }}
            tick={axisStyle}
            axisLine={{ stroke: colorsConfig.Gray1 }}
            tickLine={false}
          />
          <YAxis
            type="number"
            dataKey="return"
            name="% Rendimento medio annuo atteso"
            domain={chartConfig.returnDomain}
            ticks={chartConfig.returnTicks}
            tickFormatter={(value) => `${value}%`}
            label={{
              value: t("portfolio.efficientFrontierLeftLabel"),
              angle: -90,
              position: "center",
              dx: -40,
              ...labelStyle,
            }}
            tick={(props) => {
              const { x, y, payload } = props;
              if (payload.value === 0) return null;
              return (
                <g transform={`translate(${x},${y})`}>
                  <text
                    x={-10}
                    y={0}
                    dy={16}
                    textAnchor="middle"
                    fill={axisStyle.fill}
                    fontSize={axisStyle.fontSize}
                    fontFamily={axisStyle.fontFamily}
                    fontWeight={axisStyle.fontWeight}
                  >
                    {`${payload.value}%`}
                  </text>
                </g>
              );
            }}
            axisLine={{ stroke: colorsConfig.Gray1 }}
            tickLine={false}
          />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              if (active && payload?.length) {
                return (
                  <div className={styles.tooltipContainer}>
                    <div className={styles.tooltipRow}>
                      VaR: {formatItalianNumber(payload[0].payload.risk)}%
                    </div>
                    <div className={styles.tooltipRow}>
                      Rendimento: {formatItalianNumber(payload[0].payload.return)}%
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />

          <Line
            type="monotone"
            data={sortedData}
            dataKey="return"
            name={t("portfolio.efficientFrontierLine1Label")}
            stroke={colorsConfig.FinecoBlack}
            strokeWidth={2}
            dot={{ fill: "white", stroke: colorsConfig.FinecoBlack, r: 6 }}
            connectNulls
            isAnimationActive={false}
          />
          {/* <Scatter
            name={t("portfolio.efficientFrontierLine2Label")}
            data={[selectedPlan1]}
            fill={colorsConfig.Green}
            r={6}
          /> */}
          <Scatter
            name={t("portfolio.efficientFrontierLine3Label")}
            data={[portfolio1]}
            fill={colorsConfig.portfolio1}
            r={6}
            isAnimationActive={false}
          />
          <Scatter
            name={t("portfolio.efficientFrontierLine4Label")}
            data={[portfolio2]}
            fill={colorsConfig.portfolio2}
            r={6}
            isAnimationActive={false}
          />
          {/* TODO i am commenting this out, but i have no clear understanding of these datas */}
          {/* <Scatter
          name={t("portfolio.efficientFrontierLine5Label")}
          data={[portfolio2]}
          fill={colorsConfig.portfolio2}
          r={6}
        /> */}
        </ComposedChart>
      </ResponsiveContainer>
      <CustomLegend />
    </>
  );
};

export default EfficientFrontierCompareChart;
